import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";
import LocationsMultiSelect from "@/components/LocationsMultiSelect";
import { projectsApi, locationsApi } from "@/lib/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";

const COLOR_OPTIONS = [
  // Inline hex on named swatches so the picker always renders the TRUE
  // palette color — under a white-label brand the amber → brand-accent
  // remap in index.css would otherwise repaint "amber" (and its ring
  // indicator) as the active brand color, causing a "picked red, got
  // amber card" mismatch.
  { key: "amber",  swatch: "", inlineStyle: { backgroundColor: "#f59e0b" } },
  { key: "rose",   swatch: "", inlineStyle: { backgroundColor: "#f43f5e" } },
  { key: "teal",   swatch: "", inlineStyle: { backgroundColor: "#14b8a6" } },
  { key: "violet", swatch: "", inlineStyle: { backgroundColor: "#8b5cf6" } },
  { key: "blue",   swatch: "", inlineStyle: { backgroundColor: "#3b82f6" } },
  // "Follow brand" — reads var(--brand-accent) live so a saved project
  // flips color when a tenant switches theme.
  { key: "brand",  swatch: "", inlineStyle: { backgroundColor: "var(--brand-accent)" } },
];

const STATUS_OPTIONS = [
  { value: "active",           label: "Active" },
  { value: "on_hold",          label: "On Hold" },
  { value: "completed",        label: "Completed" },
  { value: "ready_to_archive", label: "Ready To Archive" },
  { value: "archived",         label: "Archived" },
  { value: "cancelled",        label: "Cancelled" },
];

export default function EditProjectDialog({ open, onOpenChange, project, onSaved, onDeleted }) {
  if (!open) return null;
  return (
    <EditProjectDialogInner
      open={open}
      onOpenChange={onOpenChange}
      project={project}
      onSaved={onSaved}
      onDeleted={onDeleted}
    />
  );
}

function EditProjectDialogInner({ open, onOpenChange, project, onSaved, onDeleted }) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const initialIds =
    project.location_ids && project.location_ids.length
      ? project.location_ids
      : project.location_id
        ? [project.location_id]
        : [];

  const [form, setForm] = useState({
    name: project.name || "",
    client: project.client || "",
    status: project.status || "active",
    job_number: project.job_number || "",
    director: project.director || "",
    start_date: project.start_date || "",
    shoot_date: project.shoot_date || "",
    description: project.description || "",
    cover_color: project.cover_color || "amber",
    location_ids: initialIds,
  });
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addLocOpen, setAddLocOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLocations(await locationsApi.list());
      } catch {
        toast.error("Failed to load locations");
      }
    })();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    setSaving(true);
    try {
      const updated = await projectsApi.update(project.id, {
        name: form.name,
        client: form.client,
        status: form.status,
        job_number: form.job_number,
        director: form.director,
        start_date: form.start_date,
        shoot_date: form.shoot_date,
        description: form.description,
        cover_color: form.cover_color,
        location_ids: form.location_ids,
        // Keep legacy single-location string mirrored for display compatibility
        location:
          locations.find((l) => l.id === form.location_ids[0])?.name || "",
        location_id: form.location_ids[0] || "",
      });
      onSaved(updated);
      toast.success("Project updated");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">
              Edit Project
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Update production details. Changes save to the project and the call sheet pulls them automatically.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-1">
                <Label className="text-overline text-zinc-500">Job #</Label>
                <Input
                  data-testid="edit-project-job-number"
                  value={form.job_number}
                  onChange={(e) => setForm({ ...form, job_number: e.target.value })}
                  placeholder="2026-014"
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 font-mono"
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-overline text-zinc-500">
                  Project Name<span className="text-amber-500 ml-1">*</span>
                </Label>
                <Input
                  data-testid="edit-project-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-overline text-zinc-500">Director</Label>
                <Input
                  data-testid="edit-project-director"
                  value={form.director}
                  onChange={(e) => setForm({ ...form, director: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-overline text-zinc-500">Start Date</Label>
                <Input
                  data-testid="edit-project-start-date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
                <p className="text-[10px] text-zinc-600">Day 0 for templates & plan anchor</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-overline text-zinc-500">
                  Shoot Date <span className="text-zinc-600">(primary)</span>
                </Label>
                <Input
                  data-testid="edit-project-date"
                  type="date"
                  value={form.shoot_date}
                  onChange={(e) => setForm({ ...form, shoot_date: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
                <p className="text-[10px] text-zinc-600">Or use &ldquo;Add shoot date&rdquo; in the header for multi-shoot</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-overline text-zinc-500">Client</Label>
                <Input
                  data-testid="edit-project-client"
                  value={form.client}
                  onChange={(e) => setForm({ ...form, client: e.target.value })}
                  placeholder="A24, Apple, etc."
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-overline text-zinc-500">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger
                    data-testid="edit-project-status"
                    className="bg-zinc-950 border-zinc-800 focus:ring-amber-500"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem
                        key={s.value}
                        value={s.value}
                        className="focus:bg-zinc-900"
                      >
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Locations</Label>
              <LocationsMultiSelect
                value={form.location_ids}
                onChange={(ids) => setForm({ ...form, location_ids: ids })}
                locations={locations}
                onAddNew={() => setAddLocOpen(true)}
                testIdPrefix="edit-project-loc"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Description</Label>
              <Textarea
                data-testid="edit-project-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[70px]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-overline text-zinc-500">Cover Tone</Label>
              <div className="flex gap-2">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    type="button"
                    key={c.key}
                    data-testid={`edit-color-${c.key}`}
                    onClick={() => setForm({ ...form, cover_color: c.key })}
                    style={c.inlineStyle}
                    title={c.key === "brand" ? "Follow brand" : c.key}
                    className={`w-8 h-8 rounded-sm transition-all ${c.swatch} ${
                      form.cover_color === c.key
                        ? "ring-2 ring-offset-2 ring-offset-zinc-950 ring-amber-500"
                        : "opacity-60 hover:opacity-100"
                    }`}
                  />
                ))}
              </div>
            </div>
            <DialogFooter className="pt-2 sm:justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                data-testid="delete-project-btn"
                onClick={() => setDeleteOpen(true)}
                className="bg-transparent border-rose-900/60 text-rose-300 hover:bg-rose-950/40 hover:border-rose-600 hover:text-rose-200 rounded-sm gap-2"
              >
                <Trash2 className="w-4 h-4" /> Delete Project
              </Button>
              <div className="flex gap-2 sm:ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-project-btn"
                  disabled={saving}
                  className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
                >
                  {saving ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        project={project}
        onDeleted={() => {
          setDeleteOpen(false);
          onOpenChange(false);
          onDeleted?.(project.id);
        }}
      />

      <InlineLocationDialog
        open={addLocOpen}
        onOpenChange={setAddLocOpen}
        onCreated={(loc) => {
          setLocations((prev) =>
            [...prev, loc].sort((a, b) => a.name.localeCompare(b.name))
          );
          setForm((f) => ({
            ...f,
            location_ids: [...(f.location_ids || []), loc.id],
          }));
          setAddLocOpen(false);
          toast.success(`Added "${loc.name}" to library`);
        }}
      />
    </>
  );
}

function DeleteProjectDialog({ open, onOpenChange, project, onDeleted }) {
  const [phase, setPhase] = useState("checking"); // checking | blocked | confirm | deleting
  const [gate, setGate] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase("checking");
    setGate(null);
    (async () => {
      try {
        const status = await projectsApi.gearCheckoutStatus(project.id);
        if (cancelled) return;
        setGate(status);
        if (!status.gear_reachable) {
          setPhase("blocked");
        } else if ((status.checked_out_count || 0) > 0) {
          setPhase("blocked");
        } else {
          setPhase("confirm");
        }
      } catch (e) {
        if (cancelled) return;
        // Preflight endpoint itself errored — surface as blocked (strict mode).
        setGate({
          checked_out_count: 0,
          gear_reachable: false,
          error: e?.response?.data?.detail || e?.message || "Gear preflight failed",
        });
        setPhase("blocked");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, project.id]);

  const handleConfirmDelete = async () => {
    setPhase("deleting");
    try {
      await projectsApi.remove(project.id);
      toast.success(`Deleted "${project.name}"`);
      onDeleted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Server-side gate mirrors the client check — surface its message so
      // users see the exact reason even if the preflight said "ok" and
      // Gear state changed between preflight and delete.
      if (detail?.code === "gear_checked_out" || detail?.code === "gear_unreachable") {
        setGate({
          checked_out_count: detail.checked_out_count || 0,
          gear_reachable: detail.code !== "gear_unreachable",
          error: detail.gear_error || "",
          message: detail.message,
        });
        setPhase("blocked");
      } else {
        toast.error("Failed to delete project");
        setPhase("confirm");
      }
    }
  };

  const blocked = phase === "blocked";
  const checkedOut = gate?.checked_out_count || 0;
  const gearUnreachable = gate && gate.gear_reachable === false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="delete-project-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-rose-400" />
            Delete Project
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {project?.name ? (
              <>
                You&apos;re about to delete{" "}
                <span className="text-zinc-300 font-medium">{project.name}</span>.
              </>
            ) : (
              "Confirm deletion."
            )}
          </DialogDescription>
        </DialogHeader>

        {phase === "checking" && (
          <div
            data-testid="delete-gear-checking"
            className="flex items-center gap-3 rounded-sm border border-zinc-800 bg-zinc-900/40 px-4 py-4 text-sm text-zinc-400"
          >
            <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
            Checking Gear check-out status…
          </div>
        )}

        {blocked && (
          <div
            data-testid="delete-gear-blocked-banner"
            className="rounded-sm border-2 border-rose-600 bg-rose-950/40 px-4 py-4 space-y-2"
          >
            <div className="flex items-center gap-2 text-rose-300 font-semibold">
              <AlertTriangle className="w-5 h-5" />
              {gearUnreachable ? "Gear Unreachable" : "Cannot Delete"}
            </div>
            {gearUnreachable ? (
              <p className="text-sm text-rose-100/90 leading-relaxed">
                Cannot Delete: the Gear app is unreachable. To protect data
                integrity we require a confirmed check-out count before
                deleting a project. Try again once Gear is back online.
              </p>
            ) : (
              <p className="text-sm text-rose-100/90 leading-relaxed">
                <span className="font-semibold">Cannot Delete: Gear is still checked out.</span>{" "}
                You must return{" "}
                <span
                  data-testid="delete-gear-count"
                  className="font-mono text-rose-200 bg-rose-900/60 px-1.5 py-0.5 rounded"
                >
                  {checkedOut}
                </span>{" "}
                item{checkedOut === 1 ? "" : "s"} in the Gear App before you
                can delete this project.
              </p>
            )}
            {gate?.error ? (
              <p className="text-[11px] text-rose-300/70 font-mono break-all">
                {gate.error}
              </p>
            ) : null}
          </div>
        )}

        {(phase === "confirm" || phase === "deleting") && (
          <div
            data-testid="delete-confirm-body"
            className="rounded-sm border border-zinc-800 bg-zinc-900/40 px-4 py-4 text-sm text-zinc-300 leading-relaxed"
          >
            Are you sure you want to delete this project? This action is{" "}
            <span className="text-rose-300 font-semibold">permanent and cannot be undone</span>.
            All call sheets, tasks, shots, and files linked to this project will be removed.
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={phase === "deleting"}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
          >
            {blocked ? "Close" : "Cancel"}
          </Button>
          {!blocked && phase !== "checking" && (
            <Button
              type="button"
              data-testid="delete-project-confirm-btn"
              onClick={handleConfirmDelete}
              disabled={phase === "deleting"}
              className="bg-rose-600 text-white hover:bg-rose-500 rounded-sm gap-2"
            >
              {phase === "deleting" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" /> Delete Permanently
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InlineLocationDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", address: "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Location name is required");
      return;
    }
    setSaving(true);
    try {
      const loc = await locationsApi.create(form);
      onCreated(loc);
      setForm({ name: "", address: "" });
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Quick Add Location
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Add name &amp; address now. You can fill in parking, hospital, and contact later in Locations.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">
              Name<span className="text-amber-500 ml-1">*</span>
            </Label>
            <Input
              data-testid="inline-loc-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Studio A, Brooklyn"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Address</Label>
            <AddressAutocomplete
              testId="inline-loc-address"
              value={form.address}
              onChange={(v) => setForm({ ...form, address: v })}
              placeholder="Start typing an address…"
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="inline-loc-save"
              disabled={saving}
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
            >
              {saving ? "Saving…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
